/**
 * Chat Window Component
 *
 * The main chat panel with modern styling and Framer Motion animations.
 * Contains header with agent info, message list, and input.
 *
 * Layout modes:
 * - small    → bottom corner widget (default)
 * - expanded → premium centered modal on desktop, near-fullscreen on mobile
 *
 * The current layout is driven purely by `isExpanded` (user-controlled state in
 * WidgetApp, initialized from the static `data-fullscreen` config).
 */

import { useState, type CSSProperties } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatHeader } from './ChatHeader';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import type { WidgetMessage, Labels, AgentInfo, LongChatPosition } from '../types/widget';

interface ChatWindowProps {
    messages: WidgetMessage[];
    isLoading: boolean;
    error: string | null;
    onSendMessage: (message: string) => void;
    onClose: () => void;
    position: LongChatPosition;
    buttonSize?: number;
    offsetX?: number;
    offsetY?: number;
    labels: Labels;
    isSending?: boolean;
    agentInfo: AgentInfo | null;
    primaryColor?: string;
    isBotTyping?: boolean;
    chatMode?: 'bot' | 'agent';
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    onNewChat?: () => void;
    onClearMessages?: () => void;
    isBlocked?: boolean;
    blockedMessage?: string | null;
    remainingBlockedSeconds?: number;
}

export function ChatWindow({
                               messages,
                               isLoading,
                               error,
                               onSendMessage,
                               onClose,
                               position,
                               buttonSize = 56,
                               offsetX = 24,
                               offsetY = 24,
                               labels,
                               isSending,
                               agentInfo,
                               primaryColor = '#FF3E6C',
                               isBotTyping = false,
                               chatMode = 'bot',
                               isExpanded = false,
                               onToggleExpand,
                               onNewChat,
                               onClearMessages,
                               isBlocked = false,
                               blockedMessage = null,
                               remainingBlockedSeconds = 0,
                           }: ChatWindowProps) {
    const [showNewChatConfirm, setShowNewChatConfirm] = useState(false);

    const isTop = position.startsWith('top-');
    const isLeft = position.endsWith('-left');
    const safeButtonSize = Math.min(96, Math.max(40, Math.round(buttonSize)));
    const safeOffsetX = Math.min(200, Math.max(0, Math.round(offsetX)));
    const safeOffsetY = Math.min(200, Math.max(0, Math.round(offsetY)));
    const windowEdgeY = safeOffsetY + safeButtonSize + 16;
    const safeWindowSide = `min(${safeOffsetX}px, max(8px, calc(100vw - 388px)))`;

    const windowClass = isExpanded
        ? 'lc-window lc-window--expanded'
        : 'lc-window lc-window--small';

    const smallWindowStyle: CSSProperties = isExpanded
        ? {}
        : {
            top: isTop ? `${windowEdgeY}px` : 'auto',
            bottom: isTop ? 'auto' : `${windowEdgeY}px`,
            left: isLeft ? safeWindowSide : 'auto',
            right: isLeft ? 'auto' : safeWindowSide,
            maxHeight: `calc(100vh - ${windowEdgeY + 24}px)`,
        };

    // In agent mode, confirm before starting a new chat (a live agent may be connected).
    const handleRequestNewChat = () => {
        if (chatMode === 'agent') {
            setShowNewChatConfirm(true);
        } else {
            onNewChat?.();
        }
    };

    const confirmNewChat = () => {
        setShowNewChatConfirm(false);
        onNewChat?.();
    };

    return (
        <motion.div
            layout
            className={windowClass}
            style={smallWindowStyle}
            initial={{ opacity: 0, y: isTop && !isExpanded ? -16 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: isTop && !isExpanded ? -16 : 16 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
            <ChatHeader
                agentInfo={agentInfo}
                onClose={onClose}
                labels={labels}
                primaryColor={primaryColor}
                isExpanded={isExpanded}
                onToggleExpand={onToggleExpand}
                onRequestNewChat={handleRequestNewChat}
                onClearMessages={onClearMessages}
            />

            {error && (
                <div className="lc-px-4 lc-py-3 lc-bg-red-50 lc-text-red-600 lc-text-sm lc-text-center">
                    {error || labels.errorState}
                </div>
            )}

            <MessageList
                messages={messages}
                isLoading={isLoading}
                labels={labels}
                primaryColor={primaryColor}
                isBotTyping={isBotTyping}
                agentName={agentInfo?.name}
                chatMode={chatMode}
            />

            <ChatInput
                onSend={onSendMessage}
                inputDisabled={isLoading}
                disabled={isLoading || isSending || (chatMode === 'bot' && isBotTyping)}
                labels={labels}
                primaryColor={primaryColor}
                isBlocked={isBlocked}
                blockedMessage={blockedMessage}
                remainingBlockedSeconds={remainingBlockedSeconds}
                chatMode={chatMode}
            />

            <AnimatePresence>
                {showNewChatConfirm && (
                    <motion.div
                        key="lc-confirm"
                        className="lc-confirm-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        onClick={() => setShowNewChatConfirm(false)}
                    >
                        <motion.div
                            className="lc-confirm-card"
                            initial={{ opacity: 0, scale: 0.95, y: 8 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 8 }}
                            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="lc-confirm-title">{labels.newChatConfirmTitle}</h3>
                            <p className="lc-confirm-text">{labels.newChatConfirmAgent}</p>
                            <div className="lc-confirm-actions">
                                <button
                                    type="button"
                                    className="lc-confirm-btn lc-confirm-btn--ghost"
                                    onClick={() => setShowNewChatConfirm(false)}
                                >
                                    {labels.cancel}
                                </button>
                                <button
                                    type="button"
                                    className="lc-confirm-btn lc-confirm-btn--primary"
                                    style={{ backgroundColor: primaryColor }}
                                    onClick={confirmNewChat}
                                >
                                    {labels.confirm}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
